/**
 * API Configuration for React Native/Expo and Web
 * Properly handles environment variables across platforms
 */

/**
 * Get the API base URL for the current environment
 * Works on web, iOS, and Android
 */
export function getApiBaseUrl(): string {
  // For web platform, check window.location first (most reliable)
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    
    // Staging: app-staging.coachcedar.com → https://staging.coachcedar.com
    if (hostname.includes('app-staging.coachcedar.com')) {
      return 'https://staging.coachcedar.com';
    }
    
    // Firebase hosting domains
    if (hostname.includes('coach-cedar-beta.web.app') || hostname === 'coachcedar.com') {
      return 'https://api.coachcedar.com';
    }
    
    // Localhost development
    if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
      return '';
    }
    
    // Production: app.coachcedar.com
    if (hostname.includes('app.coachcedar.com')) {
      return 'https://api.coachcedar.com';
    }
  }

  // For React Native/Expo environments, check for Constants
  // Only try to access if we're not in a web environment
  if (typeof window === 'undefined') {
    try {
      // @ts-ignore - expo-constants may not be available in web builds
      const Constants = require('expo-constants');
      if (Constants?.expoConfig?.extra?.apiUrl) {
        const url = Constants.expoConfig.extra.apiUrl;
        return url.endsWith('/api') ? url.slice(0, -4) : url;
      }
    } catch (e) {
      // expo-constants not available, continue with other checks
    }
  }

  // Fallback: Try EXPO_PUBLIC_API_URL (set in eas.json)
  // Note: EXPO_PUBLIC_ vars are available at build time in Expo
  const publicApiUrl = process.env.EXPO_PUBLIC_API_URL;
  if (publicApiUrl) {
    return publicApiUrl.endsWith('/api') ? publicApiUrl.slice(0, -4) : publicApiUrl;
  }

  // Default: Production API
  return 'https://api.coachcedar.com';
}

/** Normalize base URL: strip trailing /api so paths like base + '/api/chat' never double. */
function normalizeBaseUrl(url: string): string {
  if (url.endsWith('/api')) {
    return url.slice(0, -4);
  }
  return url;
}

// Export as constant for easy import (normalized to avoid double /api in native EAS builds)
export const API_BASE_URL = normalizeBaseUrl(getApiBaseUrl());
